import axios from 'axios';
import type { AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { supabase } from '../lib/supabase';
import { API_BASE_URL } from '../config/constants';
import toast from 'react-hot-toast';

// Extend axios config to allow callers to suppress toast for specific HTTP status codes.
// Usage: apiClient.get(url, { silentStatuses: [404] })
declare module 'axios' {
  interface AxiosRequestConfig {
    silentStatuses?: number[];
  }
}

export const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Include cookies for legacy API support
});

// Request interceptor to add Supabase JWT token
apiClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    try {
      // Use getSession first (faster), but if it fails, try refreshing
      const {
        data: { session },
        error: sessionError,
      } = await supabase.auth.getSession();

      if (sessionError) {
        console.warn('Session error, attempting refresh:', sessionError);
        // Try to refresh the session
        const { data: refreshData } = await supabase.auth.refreshSession();
        if (refreshData?.session?.access_token) {
          config.headers.Authorization = `Bearer ${refreshData.session.access_token}`;
        }
      } else if (session?.access_token) {
        config.headers.Authorization = `Bearer ${session.access_token}`;
      }
    } catch (error) {
      console.error('Auth error in request interceptor:', error);
      // Continue without token - let the backend handle 401
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response) {
      const { status, data } = error.response;
      const silent = (error.config?.silentStatuses ?? []) as number[];

      if (!silent.includes(status)) {
        switch (status) {
          case 401:
            // Unauthorized - redirect to login
            toast.error('Session expired. Please log in again.');
            window.location.href = '/login';
            break;
          case 403: {
            // Forbidden - usage limit exceeded
            const message = data?.message || 'Access forbidden';
            if (message.toLowerCase().includes('limit')) {
              toast.error('Usage limit exceeded. Please upgrade your subscription.');
            } else {
              toast.error(message);
            }
            break;
          }
          case 404:
            toast.error('Resource not found');
            break;
          case 500:
            toast.error('Server error. Please try again later.');
            break;
          default:
            toast.error(data?.message || 'An error occurred');
        }
      }
    } else if (error.request) {
      toast.error('Network error. Please check your connection.');
    } else {
      toast.error('An unexpected error occurred');
    }

    return Promise.reject(error);
  }
);

export default apiClient;
